/**
 * What the proposed-changes banner says, decided apart from how it looks.
 */
import type { DraftCounts } from '../useUnderwritePrimeXQuote';
import type { DescribedRow } from '../describedRows';

/**
 * Who the agent's changes are credited to. Always OMNI, never the specialist
 * agent that happened to stage them: which assistant answered is an internal
 * routing detail, and naming one put the wrong name on another's work.
 */
export const ASSISTANT_NAME = 'OMNI';

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/** The two authors a staged change can have on screen. */
export type ChangeSource = 'assistant' | 'operator';

/** What each source is called wherever a change is attributed. */
export const SOURCE_LABEL: Readonly<Record<ChangeSource, string>> = {
  assistant: ASSISTANT_NAME,
  operator: 'You',
};

/** Spoken in full, for a badge whose text is one word. */
export const SOURCE_DESCRIPTION: Readonly<Record<ChangeSource, string>> = {
  assistant: `Proposed by ${ASSISTANT_NAME}`,
  operator: 'Your edit',
};

/** OMNI first, then the underwriter: the order the badges always read in. */
const SOURCE_ORDER: readonly ChangeSource[] = ['assistant', 'operator'];

/** "5 changes". */
export function headline(counts: DraftCounts): string {
  return plural(counts.total, 'change');
}

/**
 * The banner's per-source counts, for the badges that double as the legend for
 * the ones on each row. A source with nothing staged is left out rather than
 * shown as zero.
 */
export function bannerSources(
  counts: DraftCounts
): readonly { readonly source: ChangeSource; readonly count: number }[] {
  return SOURCE_ORDER.map((source) => ({
    source,
    count: counts[source],
  })).filter(({ count }) => count > 0);
}

/**
 * Who authored the changes on one row, each once, in badge order.
 *
 * `system` is reserved and never emitted (see `countRows`), and has no badge.
 */
export function rowSources(
  changes: readonly { readonly provenance: string }[]
): readonly ChangeSource[] {
  const present = new Set(changes.map((change) => change.provenance));
  return SOURCE_ORDER.filter((source) => present.has(source));
}

/**
 * The log entries in the order they were staged, each once. What the stepper
 * walks: accept and reject are entry-scoped, so an entry is one stop even when
 * it wrote two fields.
 */
export function entryOrder(rows: readonly DescribedRow[]): readonly string[] {
  return Array.from(new Set(rows.map((row) => row.entryId)));
}

/** The next stop after `current`, wrapping. -1 for "none yet" starts at 0. */
/**
 * Where a step lands when the stop it was on has gone — accepted or rejected
 * from its row. The entry that followed it has moved into its slot, so a step
 * forward lands on that slot and a step back on the one before it.
 */
export function stepFromRemoved(
  lastPosition: number,
  direction: 1 | -1,
  length: number
): number {
  if (length === 0) {
    return -1;
  }
  if (direction === 1) {
    return Math.min(lastPosition, length) % length;
  }
  return (Math.min(lastPosition, length) - 1 + length) % length;
}

export function stepIndex(
  current: number,
  direction: 1 | -1,
  length: number
): number {
  if (length === 0) {
    return -1;
  }
  if (current < 0) {
    return direction === 1 ? 0 : length - 1;
  }
  return (current + direction + length) % length;
}

/**
 * The attribute `StagedRowActions` carries, so the banner can find the row an
 * entry sits on. Space-separated because one row can hold several entries;
 * matched with `~=`.
 */
export const STAGED_ENTRIES_ATTR = 'data-staged-entries';

export const stagedEntrySelector = (entryId: string) =>
  `[${STAGED_ENTRIES_ATTR}~="${entryId}"]`;

/** What the premium impact reads as for one delta. */
export interface PremiumDeltaView {
  /** The figure, or "No change" when the delta shows as zero. */
  readonly text: string;
  /** The arrow to draw; none for a delta that shows as zero or is unknown. */
  readonly direction: 'up' | 'down' | null;
  readonly noChange: boolean;
}

export const NO_CHANGE_LABEL = 'No change';

/**
 * How a premium delta reads, decided from the figure AS SHOWN: +0.40 rounds to
 * zero, so it says "No change" with no arrow rather than "$0" (which read as a
 * zero premium) or "+$0". `formatWhole` formats a whole-unit amount.
 */
export function premiumDeltaView(
  delta: number | null,
  formatWhole: (value: number | null) => string
): PremiumDeltaView {
  if (delta == null) {
    return { text: formatWhole(null), direction: null, noChange: false };
  }
  const shown = Math.round(delta);
  if (shown === 0) {
    return { text: NO_CHANGE_LABEL, direction: null, noChange: true };
  }
  if (shown > 0) {
    return { text: `+${formatWhole(shown)}`, direction: 'up', noChange: false };
  }
  return { text: formatWhole(shown), direction: 'down', noChange: false };
}
